/**
 * Покупка лицензий на программы без учётной записи (#242).
 *
 * Здесь вся логика, которую можно проверить без браузера: разбор ответов
 * API, перевод кодов отказа в ключи словаря, расписание опроса заказа и
 * мелочи вроде цены прописью. Страницы и формы только рисуют результат.
 *
 * Разбор ответов сделан осторожным, а не приведением типа: ручки живут в
 * РФ-контуре API и до появления ключа подписи отвечают 404 с чужим телом.
 * Приведение `as Product[]` пропустило бы `{error:…}` дальше, и страница
 * упала бы на `.map` вместо честного «продажа скоро откроется».
 */

/** Товар каталога, как его отдаёт `GET /licenses/products`. */
export interface TovarLicenzii {
  id: string;
  title: string;
  code: string;
  edition: string;
  priceCents: number;
  currency: string;
  perpetual: boolean;
  /**
   * Сколько лет обновлений даёт ключ; 0 — все версии, включая будущие.
   *
   * Это не срок работы программы. Решение владельца (#242, 01.10):
   * через 3 года ничего не выключается, человек остаётся на последней
   * версии, вышедшей до конца срока.
   */
  updatesYears: number;
  /**
   * Срок обновлений в месяцах; 0 — все версии. Главнее `updatesYears`:
   * за рубежом срочная лицензия на 6 месяцев, в целых годах это ноль.
   */
  updatesMonths: number;
  maxSeats: number;
  activation: string[];
  onSale: boolean;
  /**
   * Способы оплаты рынка этого сайта: `yookassa` на aisearch.ru,
   * `usdt_trc20` и `btc` на aisearch.tech (решение владельца 03.10.2026).
   */
  paymentMethods: string[];
}

/** Крипто-счёт: куда и сколько перевести. */
export interface SchetKripto {
  provider: string;
  address: string;
  /** Точная сумма в монетах строкой, как её отдаёт API. */
  amount: string;
  network: string;
  rate: number;
  expiresAt: string;
  /** Только BTC: сколько подтверждений сети ждём до выдачи ключа. */
  confirmations: number;
}

/** Способы оплаты криптой, которые умеет витрина. */
export const KRIPTO = ['usdt_trc20', 'btc'] as const;
export type SposobKripto = (typeof KRIPTO)[number];

/** Зарубежный рынок: оплата криптой, а не ЮKassa. */
export function kriptoRynok(tovary: Pick<TovarLicenzii, 'paymentMethods'>[]): SposobKripto[] {
  const est = new Set(tovary.flatMap((t) => t.paymentMethods));
  return KRIPTO.filter((k) => est.has(k));
}

/** Состояние заказа. Неизвестное значение сводится к `pending`. */
export type StatusZakaza = 'pending' | 'paid' | 'canceled' | 'refunded';

/** Заказ для страницы «Спасибо», `GET /licenses/orders/{id}`. */
export interface ZakazLicenzii {
  orderId: string;
  status: StatusZakaza;
  productId: string;
  title: string;
  holder: string;
  emailMasked: string;
  seats: number;
  amountCents: number;
  /** Цена до скидки; без промокода равна `amountCents`. */
  listAmountCents: number;
  /** Применённый промокод; пусто — без него. */
  promoCode: string;
  currency: string;
  /** `yookassa`, `usdt_trc20` или `btc`. */
  provider: string;
  /** Счёт, пока заказ ждёт перевод криптой. */
  invoice: SchetKripto | null;
  licenseKey: string;
  emailed: boolean;
  issuedAt: string;
}

/** Ответ на оформление: номер заказа и токен виджета ЮKassa или крипто-счёт. */
export interface OformlenieLicenzii {
  orderId: string;
  /** Пусто при оплате криптой. */
  confirmationToken: string;
  invoice: SchetKripto | null;
  amountCents: number;
  /** Цена до скидки; без промокода равна `amountCents`. */
  listAmountCents: number;
  promoCode: string;
  currency: string;
}

/**
 * Действующий промокод, `GET /licenses/promos/{code}`.
 *
 * Цены со скидкой приходят от API, а не считаются здесь из процента:
 * округление до рубля и минимальную сумму знает сервер, и витрина не
 * должна обещать «1 117,50 ₽», когда к оплате будет 1 117 ₽.
 */
export interface PromoLicenzii {
  code: string;
  percent: number;
  /** Конец действия, ISO; пусто — бессрочно. */
  endsAt: string;
  /** Цены по товарам, на которые код действует: id → до и после скидки. */
  ceny: Record<string, { listAmountCents: number; amountCents: number }>;
}

/**
 * Товары Konspekt, которые продаются на странице `/pricing/license/konspekt`.
 *
 * Отбор по коду программы, а не по списку идентификаторов: при новой
 * версии товара (`…_v2`) каталог API сменит идентификатор, и страница
 * подхватит его без правки здесь.
 */
export const KOD_KONSPEKT = 'konspekt';

/** Номер заказа — uuid. Всё остальное в адрес API не пропускаем. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Годен ли номер заказа из адреса страницы.
 *
 * Прокси подставляет номер в путь запроса к API. Без проверки
 * `../billing` или `x%2F..` превратили бы публичную ручку заказа в
 * обращение к соседним маршрутам API с нашим секретом шлюза.
 */
export function nomerZakazaGoden(id: string): boolean {
  return UUID.test(id);
}

function stroka(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function chislo(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function obekt(v: unknown): Record<string, unknown> | null {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

/**
 * Каталог из ответа API.
 *
 * Не массив (404 с телом ошибки, HTML балансировщика) — пустой список:
 * для страницы это одно и то же «продажа пока закрыта». Строки без
 * идентификатора или цены отбрасываются: купить их всё равно нельзя.
 */
export function razobratTovary(data: unknown): TovarLicenzii[] {
  if (!Array.isArray(data)) return [];
  const out: TovarLicenzii[] = [];
  for (const syroe of data) {
    const o = obekt(syroe);
    if (!o) continue;
    const id = stroka(o.id);
    const priceCents = chislo(o.price_cents);
    if (id === '' || priceCents <= 0) continue;
    out.push({
      id,
      title: stroka(o.title),
      code: stroka(o.code),
      edition: stroka(o.edition),
      priceCents,
      currency: stroka(o.currency) || 'RUB',
      perpetual: o.perpetual === true,
      updatesYears: chislo(o.updates_years),
      // Старый API месяцев не отдаёт: тогда считаем из лет.
      updatesMonths: chislo(o.updates_months) || chislo(o.updates_years) * 12,
      maxSeats: Math.max(1, chislo(o.max_seats)),
      activation: Array.isArray(o.activation)
        ? o.activation.filter((s): s is string => typeof s === 'string')
        : [],
      onSale: o.on_sale === true,
      paymentMethods: Array.isArray(o.payment_methods)
        ? o.payment_methods.filter((s): s is string => typeof s === 'string')
        : [],
    });
  }
  return out;
}

/**
 * Товары Konspekt в порядке показа: сначала «на 3 года», затем «навсегда».
 *
 * Дешёвый первым, потому что так читается разница: «а если доплатить —
 * все будущие версии». Наоборот выходило бы «навсегда, а можно хуже».
 */
export function tovaryKonspekt(vse: TovarLicenzii[]): TovarLicenzii[] {
  return vse.filter((t) => t.code === KOD_KONSPEKT).sort((a, b) => a.priceCents - b.priceCents);
}

/** Вид лицензии для текстов: срок обновлений или все версии. */
export type VidLicenzii = 'srok' | 'navsegda';

export function vidLicenzii(t: Pick<TovarLicenzii, 'updatesMonths'>): VidLicenzii {
  return t.updatesMonths > 0 ? 'srok' : 'navsegda';
}

/** Счёт из ответа API; `null`, если адреса или суммы нет. */
export function razobratSchet(data: unknown): SchetKripto | null {
  const o = obekt(data);
  if (!o) return null;
  const address = stroka(o.address);
  const amount = stroka(o.amount);
  if (address === '' || amount === '') return null;
  return {
    provider: stroka(o.provider),
    address,
    amount,
    network: stroka(o.network),
    rate: chislo(o.rate),
    expiresAt: stroka(o.expires_at),
    confirmations: chislo(o.confirmations),
  };
}

/** Открыта ли продажа: есть хотя бы один товар в продаже. */
export function prodazhaOtkryta(tovary: TovarLicenzii[]): boolean {
  return tovary.some((t) => t.onSale);
}

function status(v: unknown): StatusZakaza {
  return v === 'paid' || v === 'canceled' || v === 'refunded' ? v : 'pending';
}

/** Заказ из ответа API; `null`, если тело на заказ не похоже. */
export function razobratZakaz(data: unknown): ZakazLicenzii | null {
  const o = obekt(data);
  if (!o) return null;
  const orderId = stroka(o.order_id);
  if (orderId === '') return null;
  return {
    orderId,
    status: status(o.status),
    productId: stroka(o.product_id),
    title: stroka(o.title),
    holder: stroka(o.holder),
    emailMasked: stroka(o.email_masked),
    seats: Math.max(1, chislo(o.seats)),
    amountCents: chislo(o.amount_cents),
    listAmountCents: chislo(o.list_amount_cents) || chislo(o.amount_cents),
    promoCode: stroka(o.promo_code),
    currency: stroka(o.currency) || 'RUB',
    provider: stroka(o.provider),
    invoice: razobratSchet(o.invoice),
    licenseKey: stroka(o.license_key),
    emailed: o.emailed === true,
    issuedAt: stroka(o.issued_at),
  };
}

/** Ответ на оформление; `null`, если нечем платить: ни токена виджета, ни счёта. */
export function razobratOformlenie(data: unknown): OformlenieLicenzii | null {
  const o = obekt(data);
  if (!o) return null;
  const orderId = stroka(o.order_id);
  const confirmationToken = stroka(o.confirmation_token);
  const invoice = razobratSchet(o.invoice);
  if (orderId === '' || (confirmationToken === '' && invoice === null)) return null;
  return {
    orderId,
    confirmationToken,
    invoice,
    amountCents: chislo(o.amount_cents),
    listAmountCents: chislo(o.list_amount_cents) || chislo(o.amount_cents),
    promoCode: stroka(o.promo_code),
    currency: stroka(o.currency) || 'RUB',
  };
}

/** Код промокода так, как его хранит API: заглавными, 3–32 знака. */
const KOD_PROMO = /^[A-Z0-9_-]{3,32}$/;

/**
 * Промокод из ссылки или поля формы; пусто, если на код не похоже.
 *
 * Регистр и пробелы не важны: в ссылке и в поле его набирают как
 * угодно. Проверка до запроса — та же защита, что у номера заказа: код
 * подставляется в путь запроса к API, и `../billing` туда попасть не
 * должен.
 */
export function normalizovatPromo(s: string | null | undefined): string {
  const kod = (s ?? '').trim().toUpperCase();
  return KOD_PROMO.test(kod) ? kod : '';
}

/** Промокод из ответа API; `null`, если код не действует или тело чужое. */
export function razobratPromo(data: unknown): PromoLicenzii | null {
  const o = obekt(data);
  if (!o) return null;
  const code = stroka(o.code);
  const percent = chislo(o.percent);
  if (code === '' || percent <= 0 || !Array.isArray(o.prices)) return null;
  const ceny: PromoLicenzii['ceny'] = {};
  for (const syroe of o.prices) {
    const c = obekt(syroe);
    const id = stroka(c?.product_id);
    const amountCents = chislo(c?.amount_cents);
    const listAmountCents = chislo(c?.list_amount_cents);
    if (id === '' || amountCents <= 0 || listAmountCents < amountCents) continue;
    ceny[id] = { listAmountCents, amountCents };
  }
  if (Object.keys(ceny).length === 0) return null;
  return { code, percent, endsAt: stroka(o.ends_at), ceny };
}

/**
 * Дата конца промокода для плашки: «31 октября».
 *
 * API отдаёт момент конца (`ends_at`), а человеку нужен последний день
 * действия: код «до 01.11 00:00 МСК» действует по 31 октября. Поэтому
 * берётся момент на секунду раньше, по московскому времени — покупают
 * в рублях, и считать день по часам браузера в Калифорнии незачем.
 */
export function promoDo(endsAt: string, locale: string): string {
  const t = Date.parse(endsAt);
  if (!Number.isFinite(t)) return '';
  try {
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'long',
      timeZone: 'Europe/Moscow',
    }).format(new Date(t - 1000));
  } catch {
    return '';
  }
}

/** Код отказа из тела `{error:{code}}`; пусто, если кода нет. */
export function kodOshibki(data: unknown): string {
  const err = obekt(obekt(data)?.error);
  return stroka(err?.code);
}

/**
 * Ключ словаря `ais_licenzii.oshibki.*` для отказа API.
 *
 * Статус учитывается отдельно от кода: 404 без кода приходит, пока
 * маршрута в API нет вовсе (ключ подписи не положен), и это не «товар не
 * найден», а «продажа ещё не открыта». Человеку важна именно эта
 * разница: в первом случае виноват адрес, во втором — подождать.
 */
export function klyuchOshibki(status: number, data: unknown): string {
  const kod = kodOshibki(data);
  switch (kod) {
    case 'bad_email':
    case 'bad_holder':
    case 'bad_seats':
    case 'offer_not_accepted':
    case 'rate_limited':
    case 'not_for_sale':
    case 'promo_invalid':
      return kod;
    case 'not_found':
      return 'not_found';
  }
  if (status === 429) return 'rate_limited';
  if (status === 404 || status === 503) return 'not_for_sale';
  return 'obshchaya';
}

/**
 * Цена для показа: «1 490 ₽».
 *
 * Через Intl, а не склейкой строки: разделитель разрядов и место знака
 * валюты у языков разные, и склейка дала бы «1490₽» или «₽1 490».
 * Копейки не показываются, если их нет: «1 490,00 ₽» на витрине
 * читается как бухгалтерский документ.
 */
export function cena(cents: number, currency: string, locale: string): string {
  const rub = cents / 100;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: Number.isInteger(rub) ? 0 : 2,
    }).format(rub);
  } catch {
    return `${String(rub)} ${currency}`;
  }
}

/** Похоже ли на почту. Окончательно проверяет API, здесь — от опечаток. */
export function pochtaPohozha(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

/**
 * Опрос заказа на странице «Спасибо».
 *
 * Вебхук ЮKassa обычно приходит за секунды, но бывает и минуты:
 * банк подтверждает 3-D Secure с задержкой. Опрос раз в 3 секунды
 * первые 2 минуты, дальше реже, и через 10 минут останавливается: вечный
 * опрос из забытой вкладки — это нагрузка без смысла. После остановки
 * человек видит кнопку «Обновить» и объяснение.
 */
export const OPROS_INTERVAL_MS = 3_000;
export const OPROS_REDKO_MS = 15_000;
export const OPROS_CHASTO_DO_MS = 2 * 60_000;
export const OPROS_PREDEL_MS = 10 * 60_000;

/**
 * Через сколько опросить снова; `null` — больше не опрашивать.
 *
 * `proshlo` — сколько времени прошло с начала ожидания.
 */
export function sleduyushchijOpros(
  st: StatusZakaza | null,
  proshlo: number,
  kripto = false,
): number | null {
  // Окончательные состояния опрашивать незачем: оплата не «откатится».
  if (st === 'paid' || st === 'canceled' || st === 'refunded') return null;
  if (proshlo >= (kripto ? OPROS_KRIPTO_PREDEL_MS : OPROS_PREDEL_MS)) return null;
  return proshlo < OPROS_CHASTO_DO_MS ? OPROS_INTERVAL_MS : OPROS_REDKO_MS;
}

/**
 * Предел опроса при оплате криптой: 15 минут на перевод плюс подтверждения
 * сети. У биткоина это до часа, и страница, бросившая опрос через 10
 * минут, оставила бы человека с «долго» как раз тогда, когда перевод уже
 * в пути.
 */
export const OPROS_KRIPTO_PREDEL_MS = 90 * 60_000;

/**
 * Последний день обновлений: выдача + N месяцев − 1 день.
 *
 * Та же формула, что у сервера в поле `upd` ключа (решение владельца,
 * #242): `iat` 2026-10-01 → `upd` 2029-09-30. Показывается на странице
 * заказа, чтобы человек знал дату заранее, а не узнал её из окна
 * программы через три года.
 *
 * Считается в UTC: сервер пишет `issued_at` в UTC, и местный часовой
 * пояс сдвинул бы дату на сутки около полуночи.
 */
export function obnovleniyaDo(issuedAt: string, mesyacev: number): string {
  const t = Date.parse(issuedAt);
  if (!Number.isFinite(t) || mesyacev <= 0) return '';
  const d = new Date(t);
  // Date.UTC сам переносит лишние месяцы и дни, как Go AddDate на сервере:
  // 31.08 + 6 месяцев → 03.03, минус день → 02.03. Та же дата, что в ключе.
  const konec = new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + mesyacev, d.getUTCDate() - 1),
  );
  return konec.toISOString().slice(0, 10);
}

/**
 * Предложения для разметки schema.org (`offers` у SoftwareApplication).
 *
 * Только из каталога API — тот же единственный источник, что у страницы
 * покупки. Цена в рублях числом, без копеек, если их нет: так её читает
 * Google. Товары не в продаже в разметку не попадают: обещать сниппетом
 * то, что нельзя купить, — повод снять расширенный результат.
 */
export function predlozheniyaDlyaRazmetki(
  tovary: TovarLicenzii[],
  kupitUrl: string,
): Record<string, unknown>[] {
  return tovary
    .filter((t) => t.onSale)
    .map((t) => ({
      '@type': 'Offer',
      name: t.title,
      price: (t.priceCents / 100).toFixed(Number.isInteger(t.priceCents / 100) ? 0 : 2),
      priceCurrency: t.currency,
      availability: 'https://schema.org/InStock',
      url: kupitUrl,
    }));
}
